import { CountryCode } from 'libphonenumber-js';
import { IMPERIAL_SYSTEM, METRIC_SYSTEM } from '../constants';

/**
 * @returns {array} List of supported markets represented as Country ISO 3166-1 alpha-3 code.
 */
export function getSupportedMarkets() {
  return [
    // Supported markets in P1
    'AUS',
    'CAN',
    'CHN',
    'FRA',
    'GBR',
    'HKG',
    'IDN',
    'JPN',
    'KOR',
    'MAC',
    'SGP',
    'THA',
    'TWN',
    'USA',
    // Supported markets in P2
    'DEU',
    'ESP',
    'IND',
    'ITA',
    'MYS',
    'PHL',
    'VNM',
  ];
}

/**
 * @param {string} locale Locale as language ISO 639-1 and country ISO 3166-1-alpha-2.
 * @returns {string} Measurement system.
 */
export function getMeasurementSystemFromLocale(market: string) {
  switch (market) {
    case 'USA':
      return IMPERIAL_SYSTEM;
    default:
      return METRIC_SYSTEM;
  }
}

const MONDAY = true;
const SUNDAY = false;
export function getFirstDayOfWeek(market: string) {
  switch (market) {
    case 'CHN':
    case 'HKG':
    case 'TWN':
    case 'KOR':
    case 'THA':
    case 'JPN':
    case 'USA':
    case 'SGP':
    case 'CAN':
    case 'MAC':
    case 'PHL':
      return SUNDAY;
    case 'IDN':
    case 'AUS':
    case 'GBR':
    case 'FRA':
    case 'DEU':
    case 'IND':
    case 'ITA':
    case 'VNM':
    case 'ESP':
    case 'MYS':
      return MONDAY;
    default:
      return SUNDAY;
  }
}

/**
 * @param {string} currency Currency ISO 4217 code. (support for 14 currencies)
 * @param {boolean} isGlobalCurrency Flag to define whether the sign will be for global case or not
 * @returns {object} Pattern for negative and positive amounts of money.
 */
export function getCurrencyPatternWithFlag(
  currency: string,
  isJPYAlternative: boolean,
  isGlobalCurrency?: boolean,
) {
  return isGlobalCurrency
    ? getCurrencyPattern(currency)
    : getDomesticCurrencyPattern(currency, isJPYAlternative);
}

/**
 * @param {string} currency Currency ISO 4217 code. (support for 14 currencies)
 * @returns {object} Pattern for negative and positive amounts of money.
 */
export function getDomesticCurrencyPattern(currency: string, isJPYAlternative: boolean) {
  switch (currency) {
    case 'JPY':
      if (isJPYAlternative) {
        return {
          positive: '{amount} {sign}',
          negative: '-{amount} {sign}',
        };
      } else {
        return {
          positive: '{amount}{sign}',
          negative: '-{amount}{sign}',
        };
      }
    default:
      return getCurrencyPattern(currency);
  }
}

/**
 * @param {string} currency Currency ISO 4217 code. (support for 14 currencies)
 * @returns {object} Pattern for negative and positive amounts of money.
 */
export function getCurrencyPattern(currency: string) {
  switch (currency) {
    case 'AUD':
    case 'CAD':
    case 'GBP':
    case 'JPY':
    case 'KRW':
    case 'THB':
    case 'TWD':
      return {
        positive: '{sign}{amount}',
        negative: '-{sign}{amount}',
      };
    case 'CNY':
      return {
        positive: '{sign}{amount}',
        negative: '{sign}-{amount}',
      };

    case 'INR':
      return {
        positive: '{sign} {amount}',
        negative: '{sign} -{amount}',
      };
    case 'EUR':
    case 'VND':
      return {
        positive: '{amount} {sign}',
        negative: '-{amount} {sign}',
      };
    case 'HKD':
    case 'IDR':
    case 'MOP':
    case 'SGD':
    case 'USD':
    case 'PHP':
    case 'MYR':
      return {
        positive: '{sign}{amount}',
        negative: '({sign}{amount})',
      };
    default:
      // TODO Andres: decide a better fallback approach
      return {
        positive: '{sign}{amount}',
        negative: '-{sign}{amount}',
      };
  }
}

/**
 * @param {string} currency Currency ISO 4217 code. (support for 110 currencies)
 * @returns {string} Domestic currency sign.
 */
export function getDomesticCurrencySign(currency: string) {
  switch (currency) {
    case 'AFN':
      return '؋';
    case 'ALL':
      return 'Lek';
    case 'ANG':
      return 'ƒ';
    case 'ARS':
      return '$';
    case 'AUD':
      return '$';
    case 'AWG':
      return 'ƒ';
    case 'AZN':
      return 'ман';
    case 'BAM':
      return 'KM';
    case 'BBD':
      return '$';
    case 'BGN':
      return 'лв';
    case 'BMD':
      return '$';
    case 'BND':
      return '$';
    case 'BOB':
      return '$b';
    case 'BRL':
      return 'R$';
    case 'BSD':
      return '$';
    case 'BWP':
      return 'P';
    case 'BYN':
      return 'Br';
    case 'BZD':
      return 'BZ$';
    case 'CAD':
      return '$';
    case 'CHF':
      return 'CHF';
    case 'CLP':
      return '$';
    case 'CNY':
      return '¥';
    case 'COP':
      return '$';
    case 'CRC':
      return '₡';
    case 'CUP':
      return '₱';
    case 'CZK':
      return 'Kč';
    case 'DKK':
      return 'kr';
    case 'DOP':
      return 'RD$';
    case 'EGP':
      return '£';
    case 'EUR':
      return '€';
    case 'FJD':
      return '$';
    case 'FKP':
      return '£';
    case 'GBP':
      return '£';
    case 'GGP':
      return '£';
    case 'GHS':
      return '¢';
    case 'GIP':
      return '£';
    case 'GTQ':
      return 'Q';
    case 'GYD':
      return '$';
    case 'HKD':
      return '$';
    case 'HNL':
      return 'L';
    case 'HRK':
      return 'kn';
    case 'HUF':
      return 'Ft';
    case 'IDR':
      return 'Rp';
    case 'ILS':
      return '₪';
    case 'IMP':
      return '£';
    case 'INR':
      return '₹';
    case 'IRR':
      return '﷼';
    case 'ISK':
      return 'kr';
    case 'JEP':
      return '£';
    case 'JMD':
      return 'J$';
    case 'JPY':
      return '円';
    case 'KGS':
      return 'лв';
    case 'KHR':
      return '៛';
    case 'KPW':
      return '₩';
    case 'KRW':
      return '₩';
    case 'KYD':
      return '$';
    case 'KZT':
      return 'лв';
    case 'LAK':
      return '₭';
    case 'LBP':
      return '£';
    case 'LKR':
      return '₨';
    case 'LRD':
      return '$';
    case 'MKD':
      return 'ден';
    case 'MNT':
      return '₮';
    case 'MOP':
      return 'MOP';
    case 'MUR':
      return '₨';
    case 'MXN':
      return '$';
    case 'MYR':
      return 'RM';
    case 'MZN':
      return 'MT';
    case 'NAD':
      return '$';
    case 'NGN':
      return '₦';
    case 'NIO':
      return 'C$';
    case 'NOK':
      return 'kr';
    case 'NPR':
      return '₨';
    case 'NZD':
      return '$';
    case 'OMR':
      return '﷼';
    case 'PAB':
      return 'B/.';
    case 'PEN':
      return 'S/.';
    case 'PHP':
      return 'PhP';
    case 'PKR':
      return '₨';
    case 'PLN':
      return 'zł';
    case 'PYG':
      return 'Gs';
    case 'QAR':
      return '﷼';
    case 'RON':
      return 'lei';
    case 'RSD':
      return 'Дин.';
    case 'RUB':
      return '₽';
    case 'SAR':
      return '﷼';
    case 'SBD':
      return '$';
    case 'SCR':
      return '₨';
    case 'SEK':
      return 'kr';
    case 'SGD':
      return '$';
    case 'SHP':
      return '£';
    case 'SOS':
      return 'S';
    case 'SRD':
      return '$';
    case 'SVC':
      return '$';
    case 'SYP':
      return '£';
    case 'THB':
      return '฿';
    case 'TRY':
      return '₺';
    case 'TTD':
      return 'TT$';
    case 'TVD':
      return '$';
    case 'TWD':
      return '$';
    case 'UAH':
      return '₴';
    case 'USD':
      return '$';
    case 'UYU':
      return '$U';
    case 'UZS':
      return 'лв';
    case 'VEF':
      return 'Bs';
    case 'VND':
      return '₫';
    case 'XCD':
      return '$';
    case 'YER':
      return '﷼';
    case 'ZAR':
      return 'R';
    case 'ZWD':
      return 'Z$';
    default:
      // TODO Andres: decide a better fallback approach
      return currency;
  }
}

/**
 * @param {string} currency Currency ISO 4217 code.
 * @returns {string} Global currency sign.
 */
export function getGlobalCurrencySign(currency: string) {
  switch (currency) {
    case 'JPY':
      return '¥';
    case 'USD':
      return 'US$';
    case 'EUR':
      return '€';
    case 'CNY':
      return 'CNY';
    case 'TWD':
      return 'NT$';
    case 'HKD':
      return 'HK$';
    case 'KRW':
      return '₩';
    case 'SGD':
      return 'S$';
    case 'AUD':
      return 'AU$';
    case 'GBP':
      return '£';
    case 'CAD':
      return 'CAD';
    case 'THB':
      return '฿';
    case 'IDR':
      return 'Rp';
    case 'MYR':
      return 'RM';
    default:
      return getDomesticCurrencySign(currency);
  }
}

/**
 * @param {string} currency Currency ISO 4217 code.
 * @param {boolean} isGlobalCurrency Flag to define whether the sign will be for global case or not
 * @returns {string} Currency sign.
 */
export function getCurrencySign(currency: string, isGlobalCurrency?: boolean) {
  return isGlobalCurrency ? getGlobalCurrencySign(currency) : getDomesticCurrencySign(currency);
}

/**
 * @param {string} currency Currency ISO 4217 code. (support for 165 currencies)
 * @returns {number} Number of decimal places.
 */
export function getCurrencyDecimals(currency: string) {
  switch (currency) {
    case 'AED':
    case 'AFN':
    case 'ALL':
    case 'AMD':
    case 'ANG':
    case 'AOA':
    case 'ARS':
    case 'AUD':
    case 'AWG':
    case 'AZN':
    case 'BAM':
    case 'BBD':
    case 'BDT':
    case 'BGN':
    case 'BMD':
    case 'BND':
    case 'BOB':
    case 'BOV':
    case 'BRL':
    case 'BSD':
    case 'BTN':
    case 'BWP':
    case 'BYN':
    case 'BZD':
    case 'CAD':
    case 'CDF':
    case 'CHE':
    case 'CHF':
    case 'CHW':
    case 'CNY':
    case 'COP':
    case 'COU':
    case 'CRC':
    case 'CUC':
    case 'CUP':
    case 'CZK':
    case 'DKK':
    case 'DOP':
    case 'DZD':
    case 'EGP':
    case 'ERN':
    case 'ETB':
    case 'EUR':
    case 'FJD':
    case 'FKP':
    case 'GBP':
    case 'GEL':
    case 'GHS':
    case 'GIP':
    case 'GMD':
    case 'GTQ':
    case 'GYD':
    case 'HKD':
    case 'HNL':
    case 'HRK':
    case 'HTG':
    case 'HUF':
    case 'IDR':
    case 'ILS':
    case 'INR':
    case 'IRR':
    case 'JMD':
    case 'KES':
    case 'KGS':
    case 'KHR':
    case 'KPW':
    case 'KYD':
    case 'KZT':
    case 'LAK':
    case 'LBP':
    case 'LKR':
    case 'LRD':
    case 'LSL':
    case 'MAD':
    case 'MDL':
    case 'MKD':
    case 'MMK':
    case 'MNT':
    case 'MOP':
    case 'MUR':
    case 'MVR':
    case 'MWK':
    case 'MXN':
    case 'MXV':
    case 'MYR':
    case 'MZN':
    case 'NAD':
    case 'NGN':
    case 'NIO':
    case 'NOK':
    case 'NPR':
    case 'NZD':
    case 'PAB':
    case 'PEN':
    case 'PGK':
    case 'PHP':
    case 'PKR':
    case 'PLN':
    case 'QAR':
    case 'RON':
    case 'RSD':
    case 'RUB':
    case 'SAR':
    case 'SBD':
    case 'SCR':
    case 'SDG':
    case 'SEK':
    case 'SGD':
    case 'SHP':
    case 'SLL':
    case 'SOS':
    case 'SRD':
    case 'SSP':
    case 'STD':
    case 'SVC':
    case 'SYP':
    case 'SZL':
    case 'THB':
    case 'TJS':
    case 'TMT':
    case 'TOP':
    case 'TRY':
    case 'TTD':
    case 'TWD':
    case 'TZS':
    case 'UAH':
    case 'USD':
    case 'USN':
    case 'UYU':
    case 'UZS':
    case 'VEF':
    case 'WST':
    case 'XCD':
    case 'YER':
    case 'ZAR':
    case 'ZMW':
    case 'ZWL':
      return 2;
    case 'BIF':
    case 'CLP':
    case 'CVE':
    case 'DJF':
    case 'GNF':
    case 'ISK':
    case 'JPY':
    case 'KMF':
    case 'KRW':
    case 'PYG':
    case 'RWF':
    case 'UGX':
    case 'UYI':
    case 'VND':
    case 'VUV':
    case 'XAF':
    case 'XOF':
    case 'XPF':
      return 0;
    case 'BHD':
    case 'IQD':
    case 'JOD':
    case 'KWD':
    case 'LYD':
    case 'OMR':
    case 'TND':
      return 3;
    case 'CLF':
      return 4;
    case 'MGA':
    case 'MRO':
      return 1;

    default:
      // TODO Andres: decide a better fallback approach
      return 2;
  }
}

/**
 * @param locale Locale as language ISO 639-1 and country ISO 3166-1-alpha-2. (support for 15 locales)
 * @returns {string} Full name format pattern.
 */
export function getNameFormatFromLocale(market: string) {
  switch (market) {
    case 'USA':
    case 'THA':
    case 'SGP':
    case 'AUS':
    case 'IDN':
    case 'CAN':
    case 'GBR':
    case 'FRA':
    case 'PHL':
    case 'IND':
    case 'DEU':
    case 'ITA':
    case 'ESP':
    case 'MYS':
      return '{first}, {last}';
    case 'HKG':
    case 'TWN':
    case 'KOR':
    case 'JPN':
    case 'CHN':
    case 'MAC':
    case 'VNM':
      return '{last}, {first}';
    default:
      // TODO Andres: decide a better fallback approach
      return '{first} {last}';
  }
}
// const COUNTRY = 'country';
// const POSTCODE = 'postcode';
// const ADDRESS = 'address';

export function getAddressFormatFromLocale(market: string) {
  switch (market) {
    case 'HKG':
    case 'TWN':
    case 'KOR':
    case 'JPN':
    case 'CHN':
    case 'MAC':
      return `{country}, {postcode}, {address}`;
    default:
      return `{address}, {postcode}, {country}`;
  }
}

/**
 * @param market Locale as language ISO 639-1 and country ISO 3166-1-alpha-2. (support for 15 locales)
 * @returns {object} Pattern for number thousands and decimals separators.
 */
export function getNumberFormatSeparators(market: string) {
  switch (market) {
    case 'IDN':
    case 'DEU':
    case 'ITA':
    case 'VNM':
    case 'ESP':
      return {
        thousands: '.',
        decimals: ',',
      };
    case 'CHN':
    case 'HKG':
    case 'TWN':
    case 'THA':
    case 'KOR':
    case 'USA':
    case 'SGP':
    case 'AUS':
    case 'CAN':
    case 'GBR':
    case 'MAC':
    case 'PHL':
    case 'IND':
    case 'MYS':
      return {
        thousands: ',',
        decimals: '.',
      };
    case 'FRA':
      return {
        thousands: '\u00A0',
        decimals: ',',
      };
    default:
      return {
        thousands: ',',
        decimals: '.',
      };
  }
}

/**
 * @param {string} alpha2 Country ISO 3166-1 alpha-2 code.
 * @returns {string} Country ISO 3166-1 alpha-3 code.
 * @url https://en.wikipedia.org/wiki/ISO_3166-1
 */
export function getCountryCodeIsoAlpha3FromIsoAlpha2(alpha2: CountryCode) {
  const countryCodeIsoAlpha3FromIsoAlpha2: { [key: string]: string } = {
    AD: 'AND',
    AE: 'ARE',
    AF: 'AFG',
    AG: 'ATG',
    AI: 'AIA',
    AL: 'ALB',
    AM: 'ARM',
    AO: 'AGO',
    // AQ: 'ATA',
    AR: 'ARG',
    AS: 'ASM',
    AT: 'AUT',
    AU: 'AUS',
    AW: 'ABW',
    AX: 'ALA',
    AZ: 'AZE',
    BA: 'BIH',
    BB: 'BRB',
    BD: 'BGD',
    BE: 'BEL',
    BF: 'BFA',
    BG: 'BGR',
    BH: 'BHR',
    BI: 'BDI',
    BJ: 'BEN',
    BL: 'BLM',
    BM: 'BMU',
    BN: 'BRN',
    BO: 'BOL',
    BQ: 'BES',
    BR: 'BRA',
    BS: 'BHS',
    BT: 'BTN',
    // BV: 'BVT',
    BW: 'BWA',
    BY: 'BLR',
    BZ: 'BLZ',
    CA: 'CAN',
    CC: 'CCK',
    CD: 'COD',
    CF: 'CAF',
    CG: 'COG',
    CH: 'CHE',
    CI: 'CIV',
    CK: 'COK',
    CL: 'CHL',
    CM: 'CMR',
    CN: 'CHN',
    CO: 'COL',
    CR: 'CRI',
    CU: 'CUB',
    CV: 'CPV',
    CW: 'CUW',
    CX: 'CXR',
    CY: 'CYP',
    CZ: 'CZE',
    DE: 'DEU',
    DJ: 'DJI',
    DK: 'DNK',
    DM: 'DMA',
    DO: 'DOM',
    DZ: 'DZA',
    EC: 'ECU',
    EE: 'EST',
    EG: 'EGY',
    EH: 'ESH',
    ER: 'ERI',
    ES: 'ESP',
    ET: 'ETH',
    FI: 'FIN',
    FJ: 'FJI',
    FK: 'FLK',
    FM: 'FSM',
    FO: 'FRO',
    FR: 'FRA',
    GA: 'GAB',
    GB: 'GBR',
    GD: 'GRD',
    GE: 'GEO',
    GF: 'GUF',
    GG: 'GGY',
    GH: 'GHA',
    GI: 'GIB',
    GL: 'GRL',
    GM: 'GMB',
    GN: 'GIN',
    GP: 'GLP',
    GQ: 'GNQ',
    GR: 'GRC',
    // GS: 'SGS',
    GT: 'GTM',
    GU: 'GUM',
    GW: 'GNB',
    GY: 'GUY',
    HK: 'HKG',
    // HM: 'HMD',
    HN: 'HND',
    HR: 'HRV',
    HT: 'HTI',
    HU: 'HUN',
    ID: 'IDN',
    IE: 'IRL',
    IL: 'ISR',
    IM: 'IMN',
    IN: 'IND',
    IO: 'IOT',
    IQ: 'IRQ',
    IR: 'IRN',
    IS: 'ISL',
    IT: 'ITA',
    JE: 'JEY',
    JM: 'JAM',
    JO: 'JOR',
    JP: 'JPN',
    KE: 'KEN',
    KG: 'KGZ',
    KH: 'KHM',
    KI: 'KIR',
    KM: 'COM',
    KN: 'KNA',
    KP: 'PRK',
    KR: 'KOR',
    KW: 'KWT',
    KY: 'CYM',
    KZ: 'KAZ',
    LA: 'LAO',
    LB: 'LBN',
    LC: 'LCA',
    LI: 'LIE',
    LK: 'LKA',
    LR: 'LBR',
    LS: 'LSO',
    LT: 'LTU',
    LU: 'LUX',
    LV: 'LVA',
    LY: 'LBY',
    MA: 'MAR',
    MC: 'MCO',
    MD: 'MDA',
    ME: 'MNE',
    MF: 'MAF',
    MG: 'MDG',
    MH: 'MHL',
    MK: 'MKD',
    ML: 'MLI',
    MM: 'MMR',
    MN: 'MNG',
    MO: 'MAC',
    MP: 'MNP',
    MQ: 'MTQ',
    MR: 'MRT',
    MS: 'MSR',
    MT: 'MLT',
    MU: 'MUS',
    MV: 'MDV',
    MW: 'MWI',
    MX: 'MEX',
    MY: 'MYS',
    MZ: 'MOZ',
    NA: 'NAM',
    NC: 'NCL',
    NE: 'NER',
    NF: 'NFK',
    NG: 'NGA',
    NI: 'NIC',
    NL: 'NLD',
    NO: 'NOR',
    NP: 'NPL',
    NR: 'NRU',
    NU: 'NIU',
    NZ: 'NZL',
    OM: 'OMN',
    PA: 'PAN',
    PE: 'PER',
    PF: 'PYF',
    PG: 'PNG',
    PH: 'PHL',
    PK: 'PAK',
    PL: 'POL',
    PM: 'SPM',
    // PN: 'PCN',
    PR: 'PRI',
    PS: 'PSE',
    PT: 'PRT',
    PW: 'PLW',
    PY: 'PRY',
    QA: 'QAT',
    RE: 'REU',
    RO: 'ROU',
    RS: 'SRB',
    RU: 'RUS',
    RW: 'RWA',
    SA: 'SAU',
    SB: 'SLB',
    SC: 'SYC',
    SD: 'SDN',
    SE: 'SWE',
    SG: 'SGP',
    SH: 'SHN',
    SI: 'SVN',
    SJ: 'SJM',
    SK: 'SVK',
    SL: 'SLE',
    SM: 'SMR',
    SN: 'SEN',
    SO: 'SOM',
    SR: 'SUR',
    SS: 'SSD',
    ST: 'STP',
    SV: 'SLV',
    SX: 'SXM',
    SY: 'SYR',
    SZ: 'SWZ',
    TC: 'TCA',
    TD: 'TCD',
    //TF: 'ATF',
    TG: 'TGO',
    TH: 'THA',
    TJ: 'TJK',
    TK: 'TKL',
    TL: 'TLS',
    TM: 'TKM',
    TN: 'TUN',
    TO: 'TON',
    TR: 'TUR',
    TT: 'TTO',
    TV: 'TUV',
    TW: 'TWN',
    TZ: 'TZA',
    UA: 'UKR',
    UG: 'UGA',
    // UM: 'UMI',
    US: 'USA',
    UY: 'URY',
    UZ: 'UZB',
    VA: 'VAT',
    VC: 'VCT',
    VE: 'VEN',
    VG: 'VGB',
    VI: 'VIR',
    VN: 'VNM',
    VU: 'VUT',
    WF: 'WLF',
    WS: 'WSM',
    YE: 'YEM',
    YT: 'MYT',
    ZA: 'ZAF',
    ZM: 'ZMB',
    ZW: 'ZWE',
  };
  return countryCodeIsoAlpha3FromIsoAlpha2[alpha2];
}

/**
 * @param {string} alpha3 Country ISO 3166-1 alpha-3 code.
 * @returns {string} Country ISO 3166-1 alpha-2 code.
 * @url https://en.wikipedia.org/wiki/ISO_3166-1
 */
export function getCountryCodeIsoAlpha2FromIsoAlpha3(alpha3: string): CountryCode {
  const countryCodeIsoAlpha2FromIsoAlpha3: { [key: string]: CountryCode } = {
    ABW: 'AW',
    AFG: 'AF',
    AGO: 'AO',
    AIA: 'AI',
    ALA: 'AX',
    ALB: 'AL',
    AND: 'AD',
    ARE: 'AE',
    ARG: 'AR',
    ARM: 'AM',
    ASM: 'AS',
    // ATA: 'AQ',
    // ATF: 'TF',
    ATG: 'AG',
    AUS: 'AU',
    AUT: 'AT',
    AZE: 'AZ',
    BDI: 'BI',
    BEL: 'BE',
    BEN: 'BJ',
    BES: 'BQ',
    BFA: 'BF',
    BGD: 'BD',
    BGR: 'BG',
    BHR: 'BH',
    BHS: 'BS',
    BIH: 'BA',
    BLM: 'BL',
    BLR: 'BY',
    BLZ: 'BZ',
    BMU: 'BM',
    BOL: 'BO',
    BRA: 'BR',
    BRB: 'BB',
    BRN: 'BN',
    BTN: 'BT',
    // BVT: 'BV',
    BWA: 'BW',
    CAF: 'CF',
    CAN: 'CA',
    CCK: 'CC',
    CHE: 'CH',
    CHL: 'CL',
    CHN: 'CN',
    CIV: 'CI',
    CMR: 'CM',
    COD: 'CD',
    COG: 'CG',
    COK: 'CK',
    COL: 'CO',
    COM: 'KM',
    CPV: 'CV',
    CRI: 'CR',
    CUB: 'CU',
    CUW: 'CW',
    CXR: 'CX',
    CYM: 'KY',
    CYP: 'CY',
    CZE: 'CZ',
    DEU: 'DE',
    DJI: 'DJ',
    DMA: 'DM',
    DNK: 'DK',
    DOM: 'DO',
    DZA: 'DZ',
    ECU: 'EC',
    EGY: 'EG',
    ERI: 'ER',
    ESH: 'EH',
    ESP: 'ES',
    EST: 'EE',
    ETH: 'ET',
    FIN: 'FI',
    FJI: 'FJ',
    FLK: 'FK',
    FRA: 'FR',
    FRO: 'FO',
    FSM: 'FM',
    GAB: 'GA',
    GBR: 'GB',
    GEO: 'GE',
    GGY: 'GG',
    GHA: 'GH',
    GIB: 'GI',
    GIN: 'GN',
    GLP: 'GP',
    GMB: 'GM',
    GNB: 'GW',
    GNQ: 'GQ',
    GRC: 'GR',
    GRD: 'GD',
    GRL: 'GL',
    GTM: 'GT',
    GUF: 'GF',
    GUM: 'GU',
    GUY: 'GY',
    HKG: 'HK',
    // HMD: 'HM',
    HND: 'HN',
    HRV: 'HR',
    HTI: 'HT',
    HUN: 'HU',
    IDN: 'ID',
    IMN: 'IM',
    IND: 'IN',
    IOT: 'IO',
    IRL: 'IE',
    IRN: 'IR',
    IRQ: 'IQ',
    ISL: 'IS',
    ISR: 'IL',
    ITA: 'IT',
    JAM: 'JM',
    JEY: 'JE',
    JOR: 'JO',
    JPN: 'JP',
    KAZ: 'KZ',
    KEN: 'KE',
    KGZ: 'KG',
    KHM: 'KH',
    KIR: 'KI',
    KNA: 'KN',
    KOR: 'KR',
    KWT: 'KW',
    LAO: 'LA',
    LBN: 'LB',
    LBR: 'LR',
    LBY: 'LY',
    LCA: 'LC',
    LIE: 'LI',
    LKA: 'LK',
    LSO: 'LS',
    LTU: 'LT',
    LUX: 'LU',
    LVA: 'LV',
    MAC: 'MO',
    MAF: 'MF',
    MAR: 'MA',
    MCO: 'MC',
    MDA: 'MD',
    MDG: 'MG',
    MDV: 'MV',
    MEX: 'MX',
    MHL: 'MH',
    MKD: 'MK',
    MLI: 'ML',
    MLT: 'MT',
    MMR: 'MM',
    MNE: 'ME',
    MNG: 'MN',
    MNP: 'MP',
    MOZ: 'MZ',
    MRT: 'MR',
    MSR: 'MS',
    MTQ: 'MQ',
    MUS: 'MU',
    MWI: 'MW',
    MYS: 'MY',
    MYT: 'YT',
    NAM: 'NA',
    NCL: 'NC',
    NER: 'NE',
    NFK: 'NF',
    NGA: 'NG',
    NIC: 'NI',
    NIU: 'NU',
    NLD: 'NL',
    NOR: 'NO',
    NPL: 'NP',
    NRU: 'NR',
    NZL: 'NZ',
    OMN: 'OM',
    PAK: 'PK',
    PAN: 'PA',
    // PCN: 'PN',
    PER: 'PE',
    PHL: 'PH',
    PLW: 'PW',
    PNG: 'PG',
    POL: 'PL',
    PRI: 'PR',
    PRK: 'KP',
    PRT: 'PT',
    PRY: 'PY',
    PSE: 'PS',
    PYF: 'PF',
    QAT: 'QA',
    REU: 'RE',
    ROU: 'RO',
    RUS: 'RU',
    RWA: 'RW',
    SAU: 'SA',
    SDN: 'SD',
    SEN: 'SN',
    SGP: 'SG',
    // SGS: 'GS',
    SHN: 'SH',
    SJM: 'SJ',
    SLB: 'SB',
    SLE: 'SL',
    SLV: 'SV',
    SMR: 'SM',
    SOM: 'SO',
    SPM: 'PM',
    SRB: 'RS',
    SSD: 'SS',
    STP: 'ST',
    SUR: 'SR',
    SVK: 'SK',
    SVN: 'SI',
    SWE: 'SE',
    SWZ: 'SZ',
    SXM: 'SX',
    SYC: 'SC',
    SYR: 'SY',
    TCA: 'TC',
    TCD: 'TD',
    TGO: 'TG',
    THA: 'TH',
    TJK: 'TJ',
    TKL: 'TK',
    TKM: 'TM',
    TLS: 'TL',
    TON: 'TO',
    TTO: 'TT',
    TUN: 'TN',
    TUR: 'TR',
    TUV: 'TV',
    TWN: 'TW',
    TZA: 'TZ',
    UGA: 'UG',
    UKR: 'UA',
    // UMI: 'UM',
    URY: 'UY',
    USA: 'US',
    UZB: 'UZ',
    VAT: 'VA',
    VCT: 'VC',
    VEN: 'VE',
    VGB: 'VG',
    VIR: 'VI',
    VNM: 'VN',
    VUT: 'VU',
    WLF: 'WF',
    WSM: 'WS',
    YEM: 'YE',
    ZAF: 'ZA',
    ZMB: 'ZM',
    ZWE: 'ZW',
  };
  return countryCodeIsoAlpha2FromIsoAlpha3[alpha3];
}
